import axios from "axios";
import { initOffline, isCacheable, cacheSet, cacheGet, offlineBarcode, offlineSearch, queueSale } from "./offline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Modo solo-lectura cuando el equipo no tiene una terminal/estación configurada.
let _terminalReadOnly = false;
export const setTerminalReadOnly = (v) => { _terminalReadOnly = !!v; };
const _writeMethods = new Set(["post", "put", "patch", "delete"]);
const _readOnlyAllow = [/\/auth\//, /^\/?config$/, /\/qz\//, /\/point\/credentials/, /\/point\/devices/, /\/terms\//, /\/stations\//, /\/products\/import\//, /\/group\//, /\/platform\//, /\/license\//, /\/attendance\//, /\/timbres\//];

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("nx_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const method = (config.method || "get").toLowerCase();
  if (_terminalReadOnly && _writeMethods.has(method)) {
    const url = config.url || "";
    const allowed = _readOnlyAllow.some((rx) => rx.test(url));
    if (!allowed) {
      return Promise.reject({ response: { data: { detail: "Terminal no configurada · modo solo lectura. Configura la terminal de este equipo para poder operar." } } });
    }
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false && !config.__skipOffline) {
    const err = new Error("offline"); err.config = config; err.isOffline = true; err.request = {};
    return Promise.reject(err);
  }
  return config;
});

// ---- Modo offline: caché de lecturas y cola de ventas ----
const _netError = (e) => !!e && !e.response && (e.isOffline || e.code === "ERR_NETWORK" || e.message === "Network Error" || e.message === "offline");
api.interceptors.response.use(
  (res) => {
    const m = (res.config?.method || "get").toLowerCase();
    if (m === "get" && isCacheable(res.config.url || "")) cacheSet(res.config.url, res.data);
    return res;
  },
  (error) => {
    const cfg = error?.config;
    if (!cfg || !_netError(error) || cfg.__skipOffline) return Promise.reject(error);
    const m = (cfg.method || "get").toLowerCase();
    const url = (cfg.url || "").replace(/^\//, "");
    const ok = (data) => Promise.resolve({ data, status: 200, statusText: "OFFLINE", headers: {}, config: cfg, offline: true });
    if (m === "get") {
      const bc = url.match(/^products\/barcode\/(.+)$/);
      if (bc) { const p = offlineBarcode(decodeURIComponent(bc[1])); return p ? ok(p) : Promise.reject({ response: { status: 404, data: { detail: "Producto no encontrado (sin conexión)" } } }); }
      const sq = url.match(/^products\/search\?q=(.+)$/) || url.match(/^products\?(?:.*&)?q=([^&]+)/);
      if (sq) return ok(offlineSearch(decodeURIComponent(sq[1])));
      const cached = cacheGet(url);
      if (cached !== undefined) return ok(cached);
      return Promise.reject({ response: { status: 503, data: { detail: "Sin conexión y sin datos guardados para esta pantalla" } } });
    }
    if (m === "post" && /^sales$/.test(url)) {
      const body = typeof cfg.data === "string" ? JSON.parse(cfg.data) : (cfg.data || {});
      return ok(queueSale(body));
    }
    return Promise.reject({ response: { status: 503, data: { detail: "Sin conexión: esta acción requiere internet. Se guardan solo ventas y consultas." } } });
  },
);
if (typeof window !== "undefined") initOffline(api);

export const money = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

export const num = (n) => new Intl.NumberFormat("es-MX").format(Number(n) || 0);

export const dateFmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
};

export const IVA_RATE = 0.16;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const fileUrl = (path) => (path ? `${API}/files/${path}` : "");
